import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * useTabOrderLock
 *
 * Leader election per route using Web Locks API.
 * Only one tab holds the lock and is editable.
 * Others poll until the lock is released.
 */

const TAB_ID_KEY = "tabId";

// Unique per JavaScript execution context (each tab/window load). Duplicated tabs get
// a new context and thus a new id, so they never inherit the leader's bypass flag.
let tabIdForThisContext = null;

function getOrCreateTabId() {
  if (tabIdForThisContext) return tabIdForThisContext;
  tabIdForThisContext = uuidv4();
  sessionStorage.setItem(TAB_ID_KEY, tabIdForThisContext);
  return tabIdForThisContext;
}

const useTabOrderLock = (routeKey) => {
  const [isFirstTab, setIsFirstTab] = useState(false);
  const [isLocked, setIsLocked] = useState(true);

  const abortRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Ensure this tab has a stable tab-id (used by Document to disable nodes in non-leader tabs)
  getOrCreateTabId();

  useEffect(() => {
    if (!navigator.locks) {
      setIsFirstTab(true);
      setIsLocked(false);
      return;
    }

    const lockName = `tab-leader-${routeKey}`;
    let cancelled = false;

    const tryAcquire = async () => {
      if (cancelled) return;

      const { held } = await navigator.locks.query();
      const alreadyHeld = held.some((l) => l.name === lockName);

      if (alreadyHeld) {
        setIsFirstTab(false);
        setIsLocked(true);
        retryTimeoutRef.current = setTimeout(tryAcquire, 1000);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await navigator.locks.request(
          lockName,
          { signal: controller.signal },
          async () => {
            setIsFirstTab(true);
            setIsLocked(false);

            return new Promise((resolve) => {
              controller.signal.addEventListener("abort", resolve);
            });
          },
        );
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[useTabOrderLock] Lock error:", err);
        }
      }
    };

    tryAcquire();

    return () => {
      cancelled = true;
      retryTimeoutRef.current && clearTimeout(retryTimeoutRef.current);
      abortRef.current?.abort();
    };
  }, [routeKey]);

  return { isFirstTab, isLocked };
};

export default useTabOrderLock;
